/**
 * API Service
 * Central service for all API calls to the MCP Platform backend
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { transformUserResponse } from '../utils/userTransformer';

// API base URL - use relative URL in production, localhost in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' 
    ? 'http://localhost:3000/api/v1' 
    : '/api/v1');

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session management
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth endpoints
export const authAPI = {
  register: async (data: {
    email: string;
    password: string;
  }) => {
    const response = await apiClient.post('/auth/register', data);
    return transformUserResponse(response.data);
  },

  login: async (email: string, password: string) => {
    const response = await apiClient.post('/auth/login', {
      email,
      password,
    });
    return transformUserResponse(response.data);
  },

  getProfile: async () => {
    const response = await apiClient.get('/auth/profile');
    return transformUserResponse(response.data);
  },

  updateProfile: async (data: { fullName?: string }) => {
    const response = await apiClient.put('/auth/profile', data);
    return transformUserResponse(response.data);
  },

  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await apiClient.post('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    return response.data;
  },

  verifyToken: async (token: string) => {
    const response = await apiClient.post('/auth/verify', null, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return transformUserResponse(response.data);
  },
};

// OAuth/Connection endpoints
export const connectionsAPI = {
  listConnections: async () => {
    const response = await apiClient.get('/oauth/services');
    return response.data;
  },

  getMcpUrl: async () => {
    const response = await apiClient.get('/auth/mcp-url');
    return response.data;
  },

  regenerateMcpUrl: async () => {
    const response = await apiClient.post('/auth/regenerate-mcp-url');
    return response.data;
  },

  initiateOAuth: async (service: string, forceReauth: boolean = false) => {
    const response = await apiClient.post('/oauth/connect', { service, forceReauth });
    return response.data;
  },

  disconnectService: async (service: string) => {
    const response = await apiClient.delete(`/oauth/disconnect/${service}`);
    return response.data;
  },

  refreshToken: async (provider: string) => {
    const response = await apiClient.post(`/oauth/refresh/${provider}`);
    return response.data;
  },

  // WHMCS-specific methods
  connectWhmcs: async (moduleUrl: string, moduleToken: string) => {
    const response = await apiClient.post('/whmcs/connect', {
      module_url: moduleUrl,
      module_token: moduleToken
    });
    return response.data;
  },

  disconnectWhmcs: async () => {
    const response = await apiClient.delete('/whmcs/disconnect');
    return response.data;
  },

  getWhmcsStatus: async () => {
    const response = await apiClient.get('/whmcs/status');
    return response.data;
  },

  testWhmcsConnection: async () => {
    const response = await apiClient.post('/whmcs/test');
    return response.data;
  },
};

// Google Analytics endpoints
export const gaAPI = {
  getUserProperties: async () => {
    const response = await apiClient.get('/ga/user-properties');
    return response.data;
  },

  updatePropertySettings: async (propertyId: string, settings: {
    propertyName: string;
    accountId?: string;
    accountName?: string;
    isEnabled: boolean;
  }) => {
    const response = await apiClient.put('/ga/property-settings', {
      propertyId,
      ...settings,
    });
    return response.data;
  },

  getEnabledProperties: async () => {
    const response = await apiClient.get('/ga/enabled-properties');
    return response.data;
  },

  selectProperty: async (propertyId: string) => {
    const response = await apiClient.post('/ga/select-property', {
      propertyId,
    });
    return response.data;
  },
};

// MCP endpoints
export const mcpAPI = {
  getInfo: async () => {
    const response = await apiClient.get('/mcp');
    return response.data;
  },

  listTools: async () => {
    const response = await apiClient.post('/mcp', {
      jsonrpc: '2.0',
      id: Date.now(),
      method: 'tools/list',
    });
    return response.data;
  },

  executeTool: async (toolName: string, args: any) => {
    const response = await apiClient.post('/mcp', {
      jsonrpc: '2.0',
      id: Date.now(),
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: args,
      },
    });
    return response.data;
  },
};

// Admin Connector Management endpoints
export const adminConnectorAPI = {
  listConnectors: async () => {
    const response = await apiClient.get('/admin/connectors');
    return response.data;
  },

  getConnector: async (key: string) => {
    const response = await apiClient.get(`/admin/connectors/${key}`);
    return response.data;
  },

  updateConnectorVisibility: async (key: string, isEnabled: boolean) => {
    const response = await apiClient.put(`/admin/connectors/${key}/visibility`, {
      is_enabled: isEnabled,
    });
    return response.data;
  },

  updateConnectorSettings: async (key: string, settings: any) => {
    const response = await apiClient.put(`/admin/connectors/${key}`, settings);
    return response.data;
  },

  syncConnectors: async () => {
    const response = await apiClient.post('/admin/connectors/sync');
    return response.data;
  },

  reorderConnectors: async (connectors: Array<{ connector_key: string }>) => {
    const response = await apiClient.put('/admin/connectors/reorder', {
      connectors,
    });
    return response.data;
  },
};

// Export types
export interface User {
  id: string;
  email: string;
  fullName?: string;
  isActive: boolean;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  mcpUrl?: string;
}

export interface Connection {
  connector: string;
  displayName: string;
  connected: boolean;
  connectedAt?: string;
  status: 'connected' | 'disconnected' | 'error';
  lastSync?: string;
  metadata?: Record<string, any>;
  is_enabled?: boolean;
  is_development?: boolean;
}

export interface MCPTool {
  name: string;
  description: string;
  inputSchema: any;
}

export interface GAProperty {
  propertyId: string;
  propertyName: string;
  accountId?: string;
  accountName?: string;
  isEnabled: boolean;
  displayOrder?: number;
  lastSelected?: string;
}

export default apiClient;